import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
} from "@mui/material";
import { ChromePicker } from "react-color";

// Icons
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import InfoTut from "../../../components/InfoTut";
import { useDispatch, useSelector } from "react-redux";
import {
  getSettings,
  saveSiteSettings,
} from "../../../reduxToolkit/features/adminPanel/webSettings";
import { getNotification } from "../../../reduxToolkit/features/adminPanel/notification";
import { colors, radii } from "../../../theme/designTokens";

const DEFAULT_PRIMARY = "#1C6B76";
const DEFAULT_ACCENT = "#E8622E";

function ColorSetting({ label, color, onChange, open, onOpenChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: `1px solid ${colors.line}`,
        borderRadius: radii.md,
        p: 1.5,
      }}
    >
      <Box
        onClick={() => onOpenChange(true)}
        sx={{
          width: 36,
          height: 36,
          borderRadius: radii.md,
          bgcolor: color,
          border: `1px solid ${colors.line}`,
          cursor: "pointer",
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.ink }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12, color: colors.ink3 }}>
          {color}
        </Typography>
      </Box>
      <IconButton size="small" onClick={() => onOpenChange(true)}>
        <InvertColorsIcon sx={{ color }} />
      </IconButton>
      <Dialog open={open} onClose={() => onOpenChange(false)}>
        <DialogContent sx={{ p: 0 }}>
          <ChromePicker
            color={color}
            disableAlpha
            onChange={(updated) => onChange(updated.hex)}
          />
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <Button onClick={() => onOpenChange(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function SiteDesignSettings() {
  const dispatch = useDispatch();
  // Scrollbar color (legacy setting, kept as-is)
  const [color, setColor] = useState("lightblue");
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Brand theme colors (drives colors.primary/accent across the whole site)
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showAccentPicker, setShowAccentPicker] = useState(false);

  // LocalData
  const localData = JSON.parse(localStorage.getItem("adminLoginInfo"));
  // Store
  const { webSettings } = useSelector((state) => state.webSettings);

  const handleSaveSiteSettings = () => {
    dispatch(
      saveSiteSettings({
        siteColorTheme: color.hex || color,
        themePrimaryColor: primaryColor,
        themeAccentColor: accentColor,
        creatorId: localData._id,
      })
    );
    setTimeout(() => {
      dispatch(getNotification());
      dispatch(getSettings());
    }, 1000);
  };

  useEffect(() => {
    setColor(webSettings.siteColorTheme || "lightblue");
    setPrimaryColor(webSettings.themePrimaryColor || DEFAULT_PRIMARY);
    setAccentColor(webSettings.themeAccentColor || DEFAULT_ACCENT);
  }, [webSettings]);

  // Store
  const { langArrFromClientSide } = useSelector((state) => state.misc);
  const translate0 = (String) => {
    let translation = "";
    if (langArrFromClientSide && langArrFromClientSide.length > 0) {
      const T = langArrFromClientSide.find((l) => l.title === String);
      if (T && T.value) {
        let obj = { ...T };
        if (T.value === "") obj.value = T.title;
        translation = T.value;
      } else if (T === undefined) translation = String;
    }
    return translation;
  };
  //! Translate

  return (
    <>
      <Typography
        sx={{ fontSize: 13, fontWeight: 700, color: colors.ink3, mb: 1.5 }}
      >
        {translate0("Brand Theme")}
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <ColorSetting
            label={translate0("Primary Color")}
            color={primaryColor}
            onChange={setPrimaryColor}
            open={showPrimaryPicker}
            onOpenChange={setShowPrimaryPicker}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ColorSetting
            label={translate0("Accent Color")}
            color={accentColor}
            onChange={setAccentColor}
            open={showAccentPicker}
            onOpenChange={setShowAccentPicker}
          />
        </Grid>
      </Grid>
      <Typography sx={{ fontSize: 12, color: colors.ink3, mb: 2 }}>
        {translate0(
          "These two colors drive the site's whole color scheme (buttons, links, highlights) across both the storefront and the admin panel. Lighter/darker shades are generated automatically."
        )}
      </Typography>

      {/* Scrollbar Theme (legacy) */}
      <Typography
        variant="body2"
        color="initial"
        sx={{ fontSize: "1rem", display: "flex", alignItems: "center" }}
      >
        <InfoTut img="tut-theme-color.png" />
        {translate0("Set Color Theme")}:{" "}
        <IconButton size="small" onClick={() => setShowColorPicker(true)}>
          <InvertColorsIcon sx={{ color: color.hex || color }} />
        </IconButton>
      </Typography>
      <Button
        variant="contained"
        fullWidth
        size="small"
        color="success"
        onClick={handleSaveSiteSettings}
      >
        {translate0("Save")}
      </Button>
      <Dialog open={showColorPicker} onClose={() => setShowColorPicker(false)}>
        <DialogContent>
          <ChromePicker
            color={color}
            disableAlpha
            onChange={(updatedColor) => setColor(updatedColor)}
          />
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <Button onClick={() => setShowColorPicker(false)} autoFocus>
            {translate0("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
